import { memo, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import {
  TicketingResolverTeamsDocument,
  TicketingResolverTeamLeadsDocument,
  type TicketingResolverTeamsQuery,
  type TicketingResolverTeamLeadsQuery,
  type TicketingResolverTeamLeadsQueryVariables,
  DeleteTicketingResolverTeamDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { trackRemoveResolverTeam } from 'modules/ticketing/lib/tracking'

import AssignTeamDialog from './AssignTeamDialog'

type ResolverTeamNode =
  TicketingResolverTeamsQuery['ticketing']['resolverTeamsConnection']['nodes'][number]

const ResolverTeamsSection = () => {
  const [showAssignDialog, setShowAssignDialog] = useState(false)

  const [performDelete] = useMutation(DeleteTicketingResolverTeamDocument, {
    extractorFn: (data) => data.ticketingMutations.deleteTicketingResolverTeam,
    refetchQueries: [TicketingResolverTeamsDocument],
  })

  const handleDelete = async (item: ResolverTeamNode) => {
    const result = await performDelete({ variables: { id: item.id } })
    const handled = handleResult(result, () => {})
    if (handled.success) {
      trackRemoveResolverTeam()
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      const errors = handled.errors ?? []
      const isBlocked = errors.some((e) => {
        const msg = ('message' in e ? e.message : '') ?? ''
        const msgs = ('messages' in e ? e.messages?.join(' ') : '') ?? ''
        return (msg + msgs).toLowerCase().includes('categor')
      })
      app.setFlash({
        type: 'wrong',
        message: isBlocked
          ? i18n.t('ticketing.settings.resolver_teams.remove_blocked')
          : i18n.t('errors.generic_error'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: TicketingResolverTeamsDocument,
      mapVariables: ({ search }) => ({
        filter: search ? { teamName: { contains: search } } : undefined,
      }),
      includePaginationVars: false,
      mapData: (data: TicketingResolverTeamsQuery) => data.ticketing.resolverTeamsConnection.nodes,
      mapPagination: (data: TicketingResolverTeamsQuery) => ({
        total:
          data.ticketing.resolverTeamsConnection.totalCount ??
          data.ticketing.resolverTeamsConnection.nodes.length,
      }),
      search: {
        enabled: true,
      },
      itemActions: (item: ResolverTeamNode) => [
        {
          label: i18n.t('ticketing.settings.resolver_teams.remove'),
          icon: Delete,
          onClick: () => handleDelete(item),
          critical: true,
        },
      ],
    },
    []
  )

  // Read team IDs from the Apollo cache populated by the data collection source above.
  // Using cache-only avoids a duplicate network request.
  const { data: resolverTeamsData } = useQuery<TicketingResolverTeamsQuery, Record<string, never>>(
    TicketingResolverTeamsDocument,
    { fetchPolicy: 'cache-only' }
  )

  const teamIds = useMemo(
    () =>
      (resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes ?? []).map((n) =>
        String(n.teamId)
      ),
    [resolverTeamsData]
  )

  const { data: leadsData } = useQuery<
    TicketingResolverTeamLeadsQuery,
    TicketingResolverTeamLeadsQueryVariables
  >(TicketingResolverTeamLeadsDocument, {
    variables: { teamIds },
    skip: teamIds.length === 0,
  })

  const leadByTeamId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const m of leadsData?.teams?.memberships ?? []) {
      if (m.teamId && m.employeeName?.fullName) {
        map[String(m.teamId)] = m.employeeName.fullName
      }
    }
    return map
  }, [leadsData])

  return (
    <ResponsivePage.Body showHeader={false}>
      <Box paddingY="s24" gap="s24">
        <SectionHeader
          title={i18n.t('ticketing.settings.resolver_teams.title')}
          description={i18n.t('ticketing.settings.resolver_teams.description')}
          action={{
            onClick: () => setShowAssignDialog(true),
            label: i18n.t('ticketing.settings.resolver_teams.assign_button'),
          }}
        />

        <OneDataCollection
          source={source}
          emptyStates={{
            'no-data': {
              emoji: '👥',
              title: i18n.t('ticketing.settings.resolver_teams.empty_title'),
              description: i18n.t('ticketing.settings.resolver_teams.empty_description'),
            },
            error: {
              title: i18n.t('ticketing.settings.resolver_teams.error_title'),
              description: i18n.t('ticketing.settings.resolver_teams.error_description'),
            },
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('ticketing.settings.resolver_teams.col_team'),
                    render: (item: ResolverTeamNode) => ({
                      type: 'team' as const,
                      value: {
                        name: item.teamName,
                        src: '',
                      },
                    }),
                  },
                  {
                    label: i18n.t('ticketing.settings.resolver_teams.col_members'),
                    render: (item: ResolverTeamNode) => String(item.memberCount),
                  },
                  {
                    label: i18n.t('ticketing.settings.resolver_teams.col_lead'),
                    render: (item: ResolverTeamNode) => leadByTeamId[String(item.teamId)] ?? '—',
                  },
                ],
              },
            },
          ]}
        />

        {showAssignDialog && <AssignTeamDialog onClose={() => setShowAssignDialog(false)} />}
      </Box>
    </ResponsivePage.Body>
  )
}

export default memo(ResolverTeamsSection)
